import french_translations from '@/translations/fr.json';

/** @typedef {'ca' | 'es' | 'eu' | 'de' | 'fr' | 'hu' | 'it' | 'sl' | 'zh_CN' | 'en'} Lang */

const TEXT_NODE = 3;

/**
 * @param {string | undefined} msgid
 * @returns {string}
 */
function cleanMessageId(msgid) {
  if (!msgid) {
    return msgid;
  }

  if (!msgid.replace) {
    return String(msgid);
  }

  return (
    msgid
      .trim()
      .replace(/"/g, '&quot;')
      .replace(/\\/g, '&#x5C;')
      // remove new lines and duplicated spaces
      .replace(/\n/g, ' ')
      .replace(/\s+/g, ' ')
  );
}

/**
 * @param {{ [msgid: string]: string | { [ctxt: string]: string } } | undefined} messages
 * @param {string} msgid
 * @param {string} msgctxt
 * @returns {string}
 */
function getTranslation(messages, msgid, msgctxt) {
  //, n = 1, context = null, defaultPlural = null){
  if (!messages) {
    // `messages are not yet available`
    return msgid;
  }

  const message = messages[msgid];

  if (!message) {
    return msgid;
  }

  // message can be a key-value object, if a context exists for this msgid
  if (!!msgctxt) {
    return message[msgctxt] ?? msgid;
  }

  // if context isn't provided, message may be a string if this msgid hasn't other version with context
  if (typeof message === 'string') {
    return message;
  }

  // otherwise, it's stored in '$$noContext' key
  // note that '$$noContext' is a reserved context :)
  return message['$$noContext'] ?? msgid;
}

/**
 * @param {Lang} lang
 * @returns
 */
function getMessages(lang) {
  if (lang === 'fr') {
    // include fr langage in app
    // and lazy load the others
    return french_translations;
  } else if (lang === 'en') {
    return import(/* webpackChunkName: "translations-en" */ `@/translations/en.json`);
  } else if (lang === 'ca') {
    return import(/* webpackChunkName: "translations-ca" */ `@/translations/ca.json`);
  } else if (lang === 'eu') {
    return import(/* webpackChunkName: "translations-eu" */ `@/translations/eu.json`);
  } else if (lang === 'it') {
    return import(/* webpackChunkName: "translations-it" */ `@/translations/it.json`);
  } else if (lang === 'de') {
    return import(/* webpackChunkName: "translations-de" */ `@/translations/de.json`);
  } else if (lang === 'es') {
    return import(/* webpackChunkName: "translations-es" */ `@/translations/es.json`);
  } else if (lang === 'hu') {
    return import(/* webpackChunkName: "translations-hu" */ `@/translations/hu.json`);
  } else if (lang === 'zh_CN') {
    return import(/* webpackChunkName: "translations-zh" */ `@/translations/zh_CN.json`);
  } else if (lang === 'sl') {
    return import(/* webpackChunkName: "translations-sl" */ `@/translations/sl.json`);
  }

  throw new Error(`Unsuported language : ${lang}`);
}

export default function install(Vue) {
  const languageVm = new Vue({
    name: 'Language',

    data: {
      current: null,
    },

    created() {
      // Non-reactive data.
      this.availableUI = {
        fr: 'Français',
        it: 'Italiano',
        de: 'Deutsch',
        en: 'English',
        es: 'Español',
        ca: 'Català',
        eu: 'Euskara',
        hu: 'Magyar',
        zh_CN: '简体中文',
        sl: 'Slovenščina',
      };

      this.availableAPI = {
        fr: 'Français',
        it: 'Italiano',
        de: 'Deutsch',
        en: 'English',
        es: 'Español',
        ca: 'Català',
        eu: 'Euskara',
        zh: '简体中文',
        sl: 'Slovenščina',
      };

      this.translations = {};
      this.current = this.$localStorage.get('current', 'fr');
    },

    methods: {
      firstLoad() {
        const lang = this.current;

        this._getMessages(this.current).then(() => {
          // dirty : simulate lang update to fire the update of page on load
          this.current = null;
          this.current = lang;
          // set html lang attribute
          document.documentElement.setAttribute('lang', lang);
        });
      },

      /** @param {Lang} lang */
      setCurrent(lang) {
        // save in locale storage
        this.$localStorage.set('current', lang);

        // if user is logged, we need to save in db his preference
        this.$user.saveLangPreference(lang);

        // then, we must defer lang setter
        // because we may need to lazy load data
        this._getMessages(lang).then(() => {
          this.current = lang;
          // set html lang attribute
          document.documentElement.setAttribute('lang', this.getIANALanguageSubtag(lang));
        });
      },

      _getMessages(lang) {
        // TODO : normally, webpack should handle this
        if (this.translations[lang] !== undefined) {
          return new Promise((resolve) => {
            resolve(this.translations[lang]);
          });
        }

        const messages = getMessages(lang);

        return new Promise((resolve) => {
          if (messages.then) {
            // messages is a promise
            messages.then((translations) => {
              this.translations[lang] = translations[lang];
              resolve(translations[lang]);
            });
          } else {
            this.translations[lang] = messages[lang];
            resolve(messages[lang]);
          }
        });
      },

      /**
       * @param {string} msgid
       * @param {string} msgctxt
       * @returns {string}
       */
      gettext(msgid, msgctxt) {
        return getTranslation(this.translations[this.current], msgid, msgctxt)
          .replace(/&quot;/g, '"')
          .replace(/&#x5C;/gi, '\\');
      },

      /**
       * @param {Lang} lang
       * @returns {string}
       */
      getIANALanguageSubtag(lang) {
        switch (lang) {
          case 'fr':
          case 'en':
          case 'ca':
          case 'eu':
          case 'it':
          case 'de':
          case 'es':
          case 'zh':
          case 'hu':
          case 'sl':
            return lang;
          case 'zh_CN':
            return 'zh';
          default:
            // eslint-disable-next-line no-console
            console.error(`Unexpected language: ${lang}`);
            return lang;
        }
      },

      /**
       * @param {Lang} lang
       * @returns {string}
       */
      getIsoLanguageTerritory(lang) {
        switch (lang) {
          case 'fr':
            return 'fr_FR';
          case 'en':
            return 'en_UK';
          case 'ca':
            return 'ca_ES';
          case 'eu':
            return 'eu_ES';
          case 'it':
            return 'it_IT';
          case 'de':
            return 'de_DE';
          case 'es':
            return 'es_ES';
          case 'hu':
            return 'hu_HU';
          case 'zh':
          case 'zh_CN':
            return 'zh_CN';
          case 'sl':
            return 'sl_SI';
          default:
            // eslint-disable-next-line no-console
            console.error(`Unexpected language: ${lang}`);
            return lang;
        }
      },

      /** @param {HTMLElement} element */
      updateElement(element) {
        if (element.dataset.msgid === undefined) {
          if (element.childNodes.length > 1 || element.firstChild.nodeType !== TEXT_NODE) {
            // eslint-disable-next-line
            console.error('v-translate must contains only text', element.childNodes);
            return;
          }

          element.dataset.msgid = cleanMessageId(element.innerText);

          const context = element.attributes.getNamedItem('translate-context');
          if (context) {
            element.dataset.msgctxt = context.value;
          }
        }

        element.innerText = this.gettext(element.dataset.msgid, element.dataset.msgctxt);
      },
    },
  });

  // An option to support translation with HTML content: `v-translate`.
  Vue.directive('translate', {
    /** @param {HTMLElement} el */
    bind(el) {
      // console.log("bind", el)
      languageVm.updateElement(el);
    },
    /** @param {HTMLElement} el */
    inserted(el) {
      // console.log("inserted", el)
      languageVm.updateElement(el);
    },
    /** @param {HTMLElement} el */
    update(el) {
      // console.log("update", el)
      languageVm.updateElement(el);
    },
  });

  Vue.prototype.$language = languageVm;
  Vue.prototype.$gettext = languageVm.gettext.bind(languageVm);

  // Fill the %{placeholders} of a translated string.
  //
  // Four places in the forum already called this — it is the standard
  // vue-gettext companion to $gettext — and it had never been defined.
  // Every one of them threw during render, and a render that throws
  // leaves the previous DOM in place: the component's state was correct,
  // the screen kept showing whatever was there before. On a discussion
  // that meant the loading spinner, forever.
  //
  // Only some discussions were affected, which is what made it look like
  // a network problem: the call sits inside "Charger les %{n} messages
  // suivants", so it renders only when a topic has more posts than the
  // twenty Discourse hydrates. Short discussions opened fine; long ones
  // never opened at all (Sixte, 2026-09-10).
  //
  // Missing keys are left as they are rather than blanked: a visible
  // %{n} says something is wrong, an empty space hides it.
  Vue.prototype.$gettextInterpolate = function (msgid, context = {}) {
    if (typeof msgid !== 'string') return '';
    return msgid.replace(/%\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(context, key) ? String(context[key]) : match
    );
  };
}
